/**
 * ChatFlows Skills Integration for AI Site Compliance Wizard
 *
 * This file contains all the skill handlers that connect the ChatFlows embed
 * to the site compliance page wizard functionality.
 *
 * Active Skills:
 * 1. save_site_type - Saves site/premises type (office, warehouse, factory, etc.)
 * 2. save_building_age - Saves building age category
 * 3. save_building_ownership - Saves ownership type (owned, leased, shared)
 * 4. save_floor_count - Saves number of floors
 * 5. save_total_area - Saves total area in sqft
 * 6. save_fire_systems - Saves fire safety systems description
 * 7. save_electrical_systems - Saves electrical systems description
 * 8. save_hvac_systems - Saves HVAC systems description
 * 9. save_water_systems - Saves water systems description
 * 10. save_gas_systems - Saves whether gas systems are present
 * 11. save_asbestos_status - Saves asbestos survey status
 * 12. save_legionella_risk - Saves legionella risk assessment status
 * 13. save_hazardous_storage - Saves hazardous materials storage info
 * 14. save_lifting_equipment - Saves lifting equipment (LOLER relevant)
 * 15. save_pressure_systems - Saves pressure systems (PSSR relevant)
 * 16. save_fixed_equipment - Saves fixed plant/machinery
 * 17. save_public_access - Saves whether public has access
 * 18. save_disabled_access - Saves DDA/accessibility info
 * 19. update_site_requirements - Updates generated requirements (add/remove/replace/amend)
 * 20. generate_site_requirements - Triggers site compliance generation
 *
 * Note: All fields are saved to the unified compliance_wizard_data table (shared with team compliance)
 */

/**
 * Register all ChatFlows skills for the site compliance wizard
 *
 * @param {Object} chatFlowsRef - Ref to ChatFlowsEmbed component
 * @param {Object} context - Context object with state and handlers
 */
export const registerSiteComplianceSkills = (chatFlowsRef, context) => {
  const {
    // State setters
    setSiteType,
    setBuildingAge,
    setBuildingOwnership,
    setFloorCount,
    setTotalAreaSqft,
    setFireSystems,
    setElectricalSystems,
    setHvacSystems,
    setWaterSystems,
    setGasSystems,
    setAsbestosStatus,
    setLegionellaRisk,
    setHazardousStorage,
    setFixedEquipment,
    setLiftingEquipment,
    setPressureSystems,
    setPublicAccess,
    setDisabledAccess,
    // Current state values
    siteType,
    buildingAge,
    buildingOwnership,
    floorCount,
    totalAreaSqft,
    fireSystems,
    electricalSystems,
    hvacSystems,
    waterSystems,
    gasSystems,
    asbestosStatus,
    legionellaRisk,
    hazardousStorage,
    fixedEquipment,
    liftingEquipment,
    pressureSystems,
    publicAccess,
    disabledAccess,
    setIsGenerating,
    projectId,
    siteRequirements,
    setSiteRequirements,
    siteComplianceService,
    generateSiteRequirementsFromWizard,
    getAllSiteEmbedData
  } = context;

  if (!chatFlowsRef.current) {
    console.warn('[SITE COMPLIANCE] ChatFlows ref not available');
    return;
  }

  console.log('[SITE COMPLIANCE] Registering ChatFlows site compliance skills...');

  // Helper function to save a single field directly
  const saveData = async (field, value) => {
    console.log(`[SITE COMPLIANCE] saveData called:`, { field, value });

    if (value === undefined) {
      console.error('[SITE COMPLIANCE] saveData: value is undefined');
      return {
        success: false,
        error: 'Missing value parameter'
      };
    }

    // Convert numeric fields
    let processedValue = value;
    if (field === 'floor_count' || field === 'total_area_sqft') {
      const numValue = parseInt(value, 10);
      if (isNaN(numValue)) {
        console.error(`[SITE COMPLIANCE] saveData: ${field} value is not a valid number:`, value);
        return {
          success: false,
          error: `Invalid ${field} value: ${value}. Must be a number.`
        };
      }
      processedValue = numValue;
      console.log(`[SITE COMPLIANCE] Converted ${field} from "${value}" to number:`, processedValue);
    }

    // Convert boolean fields
    if (field === 'gas_systems' || field === 'public_access' || field === 'disabled_access') {
      if (typeof value === 'string') {
        processedValue = value.toLowerCase() === 'true' || value.toLowerCase() === 'yes';
      } else {
        processedValue = Boolean(value);
      }
      console.log(`[SITE COMPLIANCE] Converted ${field} to boolean:`, processedValue);
    }

    try {
      // Update the appropriate state setter based on field name
      const fieldSetters = {
        site_type: setSiteType,
        building_age: setBuildingAge,
        building_ownership: setBuildingOwnership,
        floor_count: setFloorCount,
        total_area_sqft: setTotalAreaSqft,
        fire_systems: setFireSystems,
        electrical_systems: setElectricalSystems,
        hvac_systems: setHvacSystems,
        water_systems: setWaterSystems,
        gas_systems: setGasSystems,
        asbestos_status: setAsbestosStatus,
        legionella_risk: setLegionellaRisk,
        hazardous_storage: setHazardousStorage,
        fixed_equipment: setFixedEquipment,
        lifting_equipment: setLiftingEquipment,
        pressure_systems: setPressureSystems,
        public_access: setPublicAccess,
        disabled_access: setDisabledAccess
      };

      const setter = fieldSetters[field];
      if (!setter) {
        throw new Error(`Unknown field: ${field}`);
      }

      console.log(`[SITE COMPLIANCE] Updating field "${field}" to`, processedValue);
      setter(processedValue);

      // Save to database - only the changed field
      console.log(`[SITE COMPLIANCE] Saving field "${field}" to database:`, processedValue);
      await siteComplianceService.saveSiteWizardData(projectId, {
        [field]: processedValue
      });

      console.log(`[SITE COMPLIANCE] Saved ${field}="${processedValue}"`);

      // Update embed context - always send all data
      if (chatFlowsRef.current?.update && getAllSiteEmbedData) {
        const allEmbedData = getAllSiteEmbedData();
        chatFlowsRef.current.update(allEmbedData);
      }

      return {
        success: true,
        message: 'Data saved successfully'
      };
    } catch (error) {
      console.error(`[SITE COMPLIANCE] Error saving ${field}:`, error);
      return {
        success: false,
        message: 'Failed to save data'
      };
    }
  };

  // ==========================================
  // Skill 1: save_site_type
  // ==========================================
  chatFlowsRef.current.registerSkill('save_site_type', async (args) => {
    console.log('[SITE COMPLIANCE] save_site_type called:', args);
    return await saveData('site_type', args.value);
  });

  // ==========================================
  // Skill 2: save_building_age
  // ==========================================
  chatFlowsRef.current.registerSkill('save_building_age', async (args) => {
    console.log('[SITE COMPLIANCE] save_building_age called:', args);
    return await saveData('building_age', args.value);
  });

  // ==========================================
  // Skill 3: save_building_ownership
  // ==========================================
  chatFlowsRef.current.registerSkill('save_building_ownership', async (args) => {
    console.log('[SITE COMPLIANCE] save_building_ownership called:', args);
    return await saveData('building_ownership', args.value);
  });

  // ==========================================
  // Skill 4: save_floor_count
  // ==========================================
  chatFlowsRef.current.registerSkill('save_floor_count', async (args) => {
    console.log('[SITE COMPLIANCE] save_floor_count called:', args);
    return await saveData('floor_count', args.value);
  });

  // ==========================================
  // Skill 5: save_total_area
  // ==========================================
  chatFlowsRef.current.registerSkill('save_total_area', async (args) => {
    console.log('[SITE COMPLIANCE] save_total_area called:', args);
    return await saveData('total_area_sqft', args.value);
  });

  // ==========================================
  // Skill 6: save_fire_systems
  // ==========================================
  chatFlowsRef.current.registerSkill('save_fire_systems', async (args) => {
    console.log('[SITE COMPLIANCE] save_fire_systems called:', args);
    return await saveData('fire_systems', args.value);
  });

  // ==========================================
  // Skill 7: save_electrical_systems
  // ==========================================
  chatFlowsRef.current.registerSkill('save_electrical_systems', async (args) => {
    console.log('[SITE COMPLIANCE] save_electrical_systems called:', args);
    return await saveData('electrical_systems', args.value);
  });

  // ==========================================
  // Skill 8: save_hvac_systems
  // ==========================================
  chatFlowsRef.current.registerSkill('save_hvac_systems', async (args) => {
    console.log('[SITE COMPLIANCE] save_hvac_systems called:', args);
    return await saveData('hvac_systems', args.value);
  });

  // ==========================================
  // Skill 9: save_water_systems
  // ==========================================
  chatFlowsRef.current.registerSkill('save_water_systems', async (args) => {
    console.log('[SITE COMPLIANCE] save_water_systems called:', args);
    return await saveData('water_systems', args.value);
  });

  // ==========================================
  // Skill 10: save_gas_systems
  // ==========================================
  chatFlowsRef.current.registerSkill('save_gas_systems', async (args) => {
    console.log('[SITE COMPLIANCE] save_gas_systems called:', args);
    return await saveData('gas_systems', args.value);
  });

  // ==========================================
  // Skill 11: save_asbestos_status
  // ==========================================
  chatFlowsRef.current.registerSkill('save_asbestos_status', async (args) => {
    console.log('[SITE COMPLIANCE] save_asbestos_status called:', args);
    return await saveData('asbestos_status', args.value);
  });

  // ==========================================
  // Skill 12: save_legionella_risk
  // ==========================================
  chatFlowsRef.current.registerSkill('save_legionella_risk', async (args) => {
    console.log('[SITE COMPLIANCE] save_legionella_risk called:', args);
    return await saveData('legionella_risk', args.value);
  });

  // ==========================================
  // Skill 13: save_hazardous_storage
  // ==========================================
  chatFlowsRef.current.registerSkill('save_hazardous_storage', async (args) => {
    console.log('[SITE COMPLIANCE] save_hazardous_storage called:', args);
    return await saveData('hazardous_storage', args.value);
  });

  // ==========================================
  // Skill 14: save_lifting_equipment
  // ==========================================
  chatFlowsRef.current.registerSkill('save_lifting_equipment', async (args) => {
    console.log('[SITE COMPLIANCE] save_lifting_equipment called:', args);
    return await saveData('lifting_equipment', args.value);
  });

  // ==========================================
  // Skill 15: save_pressure_systems
  // ==========================================
  chatFlowsRef.current.registerSkill('save_pressure_systems', async (args) => {
    console.log('[SITE COMPLIANCE] save_pressure_systems called:', args);
    return await saveData('pressure_systems', args.value);
  });

  // ==========================================
  // Skill 16: save_fixed_equipment
  // ==========================================
  chatFlowsRef.current.registerSkill('save_fixed_equipment', async (args) => {
    console.log('[SITE COMPLIANCE] save_fixed_equipment called:', args);
    return await saveData('fixed_equipment', args.value);
  });

  // ==========================================
  // Skill 17: save_public_access
  // ==========================================
  chatFlowsRef.current.registerSkill('save_public_access', async (args) => {
    console.log('[SITE COMPLIANCE] save_public_access called:', args);
    return await saveData('public_access', args.value);
  });

  // ==========================================
  // Skill 18: save_disabled_access
  // ==========================================
  chatFlowsRef.current.registerSkill('save_disabled_access', async (args) => {
    console.log('[SITE COMPLIANCE] save_disabled_access called:', args);
    return await saveData('disabled_access', args.value);
  });

  // ==========================================
  // Skill 19: update_site_requirements
  // ==========================================
  chatFlowsRef.current.registerSkill('update_site_requirements', async (args) => {
    console.log('[SITE COMPLIANCE] update_site_requirements called with args:', args);

    // Handle different parameter formats
    let updatesParam = args.updates || args.parameters?.updates;

    if (!updatesParam) {
      console.error('[SITE COMPLIANCE] update_site_requirements: updates parameter is missing');
      return {
        success: false,
        error: 'Missing updates parameter. Expected an object with action property.',
        received: args
      };
    }

    // Parse updates if it's a JSON string
    let updates = updatesParam;
    if (typeof updates === 'string') {
      try {
        updates = JSON.parse(updates);
        console.log('[SITE COMPLIANCE] Parsed updates from JSON string:', updates);
      } catch (parseError) {
        console.error('[SITE COMPLIANCE] Failed to parse updates JSON string:', parseError);
        return {
          success: false,
          error: `Invalid JSON in updates parameter: ${parseError.message}`
        };
      }
    }

    // Handle array of updates
    if (Array.isArray(updates)) {
      console.log(`[SITE COMPLIANCE] Processing array of ${updates.length} updates...`);
      if (updates.length === 0) {
        return {
          success: false,
          error: 'Updates array is empty'
        };
      }
      updates = updates[0];
      console.log('[SITE COMPLIANCE] Processing first update from array:', updates);
    }

    const action = updates.action;

    if (!action) {
      console.error('[SITE COMPLIANCE] update_site_requirements: action property is missing');
      return {
        success: false,
        error: 'Missing action property in updates. Expected: "add", "remove", "replace", or "amend".'
      };
    }

    try {
      // Get current requirements from state
      let currentRequirements = [...(siteRequirements || [])];
      console.log(`[SITE COMPLIANCE] Processing ${action} action on requirements...`);

      // Helper function to resolve requirement ID from target
      const resolveRequirementId = (target) => {
        if (target.id) {
          return target.id;
        }
        if (target.name) {
          const found = currentRequirements.find(req => req.name === target.name);
          if (!found) {
            throw new Error(`Requirement not found with name: "${target.name}"`);
          }
          console.log(`[SITE COMPLIANCE] Found requirement by name "${target.name}": ${found.id}`);
          return found.id;
        }
        throw new Error('Target must have either "id" or "name" property');
      };

      // Process the update based on action type
      switch (action) {
        case 'add': {
          if (!updates.item) {
            throw new Error('Missing "item" property for add action');
          }

          const newRequirement = {
            name: updates.item.name,
            description: updates.item.description || '',
            category: updates.item.category || '',
            priority: updates.item.priority || 'medium',
            requirement_type: updates.item.requirement_type || updates.item.requirementType || 'inspection',
            inspection_frequency: updates.item.inspection_frequency || updates.item.inspectionFrequency || 'annual',
            status: updates.item.status || 'pending',
            regulatory_body: updates.item.regulatory_body || updates.item.regulatoryBody || '',
            regulation_reference: updates.item.regulation_reference || updates.item.regulationReference || '',
            legal_requirement: updates.item.legal_requirement || updates.item.legalRequirement || false,
            responsible_role: updates.item.responsible_role || updates.item.responsibleRole || '',
            assignment_type: updates.item.assignment_type || updates.item.assignmentType || 'space',
            notes: updates.item.notes || ''
          };

          console.log('[SITE COMPLIANCE] Adding new requirement to database...');
          const addResult = await siteComplianceService.addSiteRequirement(projectId, newRequirement);
          if (!addResult.success) {
            throw new Error(`Failed to add requirement: ${addResult.error}`);
          }

          currentRequirements.push(addResult.data);
          console.log('[SITE COMPLIANCE] Added requirement to database');
          break;
        }

        case 'remove': {
          if (!updates.target) {
            throw new Error('Missing "target" property for remove action');
          }

          const requirementId = resolveRequirementId(updates.target);
          console.log(`[SITE COMPLIANCE] Removing requirement ${requirementId} from database...`);

          const deleteResult = await siteComplianceService.deleteSiteRequirement(requirementId);
          if (!deleteResult.success) {
            throw new Error(`Failed to delete requirement: ${deleteResult.error}`);
          }

          currentRequirements = currentRequirements.filter(req => req.id !== requirementId);
          console.log('[SITE COMPLIANCE] Removed requirement from database');
          break;
        }

        case 'replace': {
          if (!updates.target) {
            throw new Error('Missing "target" property for replace action');
          }
          if (!updates.path) {
            throw new Error('Missing "path" property for replace action');
          }
          if (updates.value === undefined) {
            throw new Error('Missing "value" property for replace action');
          }

          const requirementId = resolveRequirementId(updates.target);
          const fieldPath = updates.path;
          const newValue = updates.value;

          // Map field paths to database column names
          const fieldMapping = {
            priority: 'priority',
            name: 'name',
            description: 'description',
            category: 'category',
            requirement_type: 'requirement_type',
            requirementType: 'requirement_type',
            inspection_frequency: 'inspection_frequency',
            inspectionFrequency: 'inspection_frequency',
            status: 'status',
            regulatory_body: 'regulatory_body',
            regulatoryBody: 'regulatory_body',
            regulation_reference: 'regulation_reference',
            regulationReference: 'regulation_reference',
            legal_requirement: 'legal_requirement',
            legalRequirement: 'legal_requirement',
            responsible_role: 'responsible_role',
            responsibleRole: 'responsible_role',
            assignment_type: 'assignment_type',
            assignmentType: 'assignment_type',
            notes: 'notes',
            next_due_date: 'next_due_date',
            nextDueDate: 'next_due_date'
          };

          const dbFieldName = fieldMapping[fieldPath] || fieldPath;
          console.log(`[SITE COMPLIANCE] Replacing ${fieldPath} (${dbFieldName}) for requirement ${requirementId}...`);

          const updateResult = await siteComplianceService.updateSiteRequirement(requirementId, {
            [dbFieldName]: newValue
          });

          if (!updateResult.success) {
            throw new Error(`Failed to update requirement: ${updateResult.error}`);
          }

          // Update local state
          const index = currentRequirements.findIndex(req => req.id === requirementId);
          if (index !== -1) {
            currentRequirements[index] = updateResult.data;
          }

          console.log('[SITE COMPLIANCE] Replaced field in database');
          break;
        }

        case 'amend': {
          if (!updates.target) {
            throw new Error('Missing "target" property for amend action');
          }
          if (!updates.changes || typeof updates.changes !== 'object') {
            throw new Error('Missing or invalid "changes" property for amend action');
          }

          const requirementId = resolveRequirementId(updates.target);
          const changes = updates.changes;

          // Map field names to database column names
          const fieldMapping = {
            priority: 'priority',
            name: 'name',
            description: 'description',
            category: 'category',
            requirement_type: 'requirement_type',
            requirementType: 'requirement_type',
            inspection_frequency: 'inspection_frequency',
            inspectionFrequency: 'inspection_frequency',
            status: 'status',
            regulatory_body: 'regulatory_body',
            regulatoryBody: 'regulatory_body',
            regulation_reference: 'regulation_reference',
            regulationReference: 'regulation_reference',
            legal_requirement: 'legal_requirement',
            legalRequirement: 'legal_requirement',
            responsible_role: 'responsible_role',
            responsibleRole: 'responsible_role',
            assignment_type: 'assignment_type',
            assignmentType: 'assignment_type',
            notes: 'notes',
            next_due_date: 'next_due_date',
            nextDueDate: 'next_due_date'
          };

          const dbChanges = {};
          for (const [key, value] of Object.entries(changes)) {
            const dbFieldName = fieldMapping[key] || key;
            dbChanges[dbFieldName] = value;
          }

          console.log(`[SITE COMPLIANCE] Amending multiple fields for requirement ${requirementId}...`);

          const updateResult = await siteComplianceService.updateSiteRequirement(requirementId, dbChanges);
          if (!updateResult.success) {
            throw new Error(`Failed to update requirement: ${updateResult.error}`);
          }

          // Update local state
          const index = currentRequirements.findIndex(req => req.id === requirementId);
          if (index !== -1) {
            currentRequirements[index] = updateResult.data;
          }

          console.log('[SITE COMPLIANCE] Amended requirement in database');
          break;
        }

        default:
          throw new Error(`Unknown action: ${action}. Expected: "add", "remove", "replace", or "amend".`);
      }

      // Update state with modified requirements
      console.log('[SITE COMPLIANCE] Updating local state with', currentRequirements.length, 'requirements...');
      setSiteRequirements(currentRequirements);

      // Update embed context
      if (chatFlowsRef.current?.update && getAllSiteEmbedData) {
        const allEmbedData = getAllSiteEmbedData();
        allEmbedData.generated_requirements = currentRequirements;
        chatFlowsRef.current.update(allEmbedData);
      }

      return {
        success: true,
        message: 'Data saved successfully'
      };
    } catch (error) {
      console.error('[SITE COMPLIANCE] Error updating requirements:', error);
      return {
        success: false,
        message: 'Failed to save data'
      };
    }
  });

  // ==========================================
  // Skill 20: generate_site_requirements
  // ==========================================
  chatFlowsRef.current.registerSkill('generate_site_requirements', async (args) => {
    console.log('[SITE COMPLIANCE] generate_site_requirements called:', args);

    try {
      setIsGenerating(true);

      console.log('[SITE COMPLIANCE] Starting site compliance requirements generation...');

      // Start generation in background (don't await - let it run)
      (async () => {
        try {
          await generateSiteRequirementsFromWizard();

          console.log('[SITE COMPLIANCE] Requirements generation completed');

          // Update ChatFlows - always send all data
          if (chatFlowsRef.current?.update && getAllSiteEmbedData) {
            const allEmbedData = getAllSiteEmbedData();
            console.log('[SITE COMPLIANCE] Sending updated data to ChatFlows, requirements count:', allEmbedData.generated_requirements?.length || 0);
            chatFlowsRef.current.update(allEmbedData);
          }
        } catch (error) {
          console.error('[SITE COMPLIANCE] Error generating requirements:', error);
          setIsGenerating(false);
        }
      })();

      // Return immediately as COMPLETED (generation runs in background)
      return {
        success: true,
        message: 'Data saved successfully'
      };
    } catch (error) {
      console.error('[SITE COMPLIANCE] Error starting requirements generation:', error);
      setIsGenerating(false);

      return {
        success: false,
        message: 'Failed to save data'
      };
    }
  });

  console.log('[SITE COMPLIANCE] ChatFlows site compliance skills registered (20 active skills)');
};

/**
 * Send initial context data to ChatFlows embed on load
 *
 * @param {Object} chatFlowsRef - Ref to ChatFlowsEmbed component
 * @param {Object} context - Context object with data
 */
export const sendSiteInitialContext = (chatFlowsRef, context) => {
  const {
    getAllSiteEmbedData
  } = context;

  if (!chatFlowsRef.current) {
    console.warn('[SITE COMPLIANCE] ChatFlows ref not available for context update');
    return;
  }

  console.log('[SITE COMPLIANCE] Sending initial context to ChatFlows...');

  // Use getAllSiteEmbedData to get consistent data structure
  if (getAllSiteEmbedData) {
    const contextData = getAllSiteEmbedData();

    // Send context update to iframe
    chatFlowsRef.current.update(contextData);

    console.log('[SITE COMPLIANCE] Initial context sent to ChatFlows embed');
    console.log('[SITE COMPLIANCE] Context summary:', {
      fields: Object.keys(contextData),
      requirements_count: contextData.generated_requirements?.length || 0
    });
  } else {
    console.warn('[SITE COMPLIANCE] getAllSiteEmbedData not available in context');
  }
};

/**
 * Get context summary for debugging
 */
export const getSiteContextSummary = (context) => {
  return {
    site: {
      siteType: context.siteType,
      buildingAge: context.buildingAge,
      buildingOwnership: context.buildingOwnership,
      floorCount: context.floorCount,
      totalAreaSqft: context.totalAreaSqft
    },
    systems: {
      fireSystems: context.fireSystems,
      electricalSystems: context.electricalSystems,
      waterSystems: context.waterSystems,
      gasSystems: context.gasSystems
    },
    hazards: {
      asbestosStatus: context.asbestosStatus,
      legionellaRisk: context.legionellaRisk,
      hazardousStorage: context.hazardousStorage
    },
    equipment: {
      liftingEquipment: context.liftingEquipment,
      pressureSystems: context.pressureSystems,
      fixedEquipment: context.fixedEquipment
    },
    requirements: {
      count: context.siteRequirements?.length || 0,
      generated: (context.siteRequirements?.length || 0) > 0
    },
    project: {
      id: context.projectId,
      name: context.space?.name
    }
  };
};
